import React from 'react';
import './Shimmer.css';

export function Shimmer({
  className = '',
  width,
  height,
  rounded = 'md',
  style = {},
  'aria-hidden': ariaHidden = true,
}) {
  const roundedClass = rounded ? ` shimmer--rounded-${rounded}` : '';
  return (
    <span
      className={`shimmer${roundedClass}${className ? ` ${className}` : ''}`}
      style={{ width, height, ...style }}
      aria-hidden={ariaHidden}
    />
  );
}

export function ShimmerLine({ width = '100%', height = 14, ...props }) {
  return <Shimmer width={width} height={height} rounded="sm" {...props} />;
}

function ShimmerPageShell({ children, className = '', label = '読み込み中' }) {
  return (
    <div className={`shimmer-page${className ? ` ${className}` : ''}`} role="status" aria-live="polite" aria-label={label}>
      {children}
    </div>
  );
}

export function AdminPageHeaderShimmer({ withAction = true }) {
  return (
    <div className="shimmer-page-header">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
        <ShimmerLine width="min(220px, 55%)" height={28} />
        <ShimmerLine width="min(360px, 80%)" height={14} />
      </div>
      {withAction && <Shimmer width={96} height={38} rounded="md" />}
    </div>
  );
}

export function AdminListToolbarShimmer({ selects = 3 }) {
  return (
    <div className="shimmer-toolbar">
      <Shimmer className="shimmer-toolbar__search" />
      {Array.from({ length: selects }, (_, i) => (
        <Shimmer key={`select-${i}`} className="shimmer-toolbar__select" />
      ))}
    </div>
  );
}

export function AdminCompactListShimmer({
  variant = 'user',
  rows = 8,
  embedded = false,
  showToolbar = false,
  showResultBar = false,
}) {
  const listClass = [
    'shimmer-compact-list',
    `shimmer-compact-list--${variant}`,
    embedded ? 'is-embedded' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      {showToolbar && <AdminListToolbarShimmer selects={variant === 'clinic' ? 2 : 3} />}
      {showResultBar && (
        <div className="shimmer-result-bar">
          <ShimmerLine width={64} height={16} />
          <Shimmer width={120} height={32} rounded="md" />
        </div>
      )}
      <div className={listClass}>
        <div className="shimmer-compact-list__header" aria-hidden="true">
          <ShimmerLine width="40%" height={12} />
          <ShimmerLine width="60%" height={12} />
          {!embedded && variant === 'user' && <ShimmerLine width="50%" height={12} />}
          {variant === 'clinic' && (
            <>
              <ShimmerLine width="70%" height={12} />
              <ShimmerLine width="70%" height={12} />
            </>
          )}
          <Shimmer width={20} height={20} rounded="sm" />
        </div>
        {Array.from({ length: rows }, (_, i) => (
          <div key={`row-${i}`} className="shimmer-compact-list__row">
            <ShimmerLine width={`${55 + (i % 3) * 10}%`} height={16} />
            <Shimmer width={72} height={22} rounded="full" />
            {!embedded && variant === 'user' && <ShimmerLine width={`${45 + (i % 2) * 15}%`} height={14} />}
            {variant === 'clinic' && (
              <>
                <ShimmerLine width="50%" height={14} />
                <ShimmerLine width="40%" height={14} />
              </>
            )}
            <Shimmer width={20} height={20} rounded="sm" />
          </div>
        ))}
      </div>
    </>
  );
}

export function AdminListPageShimmer(props) {
  return (
    <ShimmerPageShell label="一覧を読み込み中">
      <AdminPageHeaderShimmer withAction />
      <AdminCompactListShimmer showToolbar showResultBar {...props} />
    </ShimmerPageShell>
  );
}

export function AdminDashboardShimmer() {
  return (
    <ShimmerPageShell label="ダッシュボードを読み込み中">
      <AdminPageHeaderShimmer withAction={false} />
      <div className="shimmer-dashboard-grid">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={`card-${i}`} className="shimmer-dashboard-card">
            <ShimmerLine width="40%" height={16} />
            <Shimmer width="45%" height={36} rounded="sm" />
            <ShimmerLine width="30%" height={12} />
            <ShimmerLine width="80%" height={12} />
            <ShimmerLine width="65%" height={12} />
          </div>
        ))}
      </div>
      <div className="shimmer-dashboard-card">
        <ShimmerLine width="28%" height={18} />
        <div className="shimmer-health-grid">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={`health-${i}`} className="shimmer-health-card">
              <ShimmerLine width="60%" height={12} />
              <Shimmer width="50%" height={28} rounded="sm" />
            </div>
          ))}
        </div>
      </div>
      <div className="shimmer-dashboard-card">
        <ShimmerLine width="24%" height={18} />
        <div className="shimmer-card-grid">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={`product-${i}`} className="shimmer-card">
              <ShimmerLine width="55%" height={16} />
              <ShimmerLine width="80%" height={12} />
              <Shimmer width={88} height={22} rounded="full" />
            </div>
          ))}
        </div>
      </div>
      <div className="shimmer-quick-actions">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={`qa-${i}`} className="shimmer-quick-action">
            <Shimmer width={40} height={40} rounded="md" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
              <ShimmerLine width="50%" height={14} />
              <ShimmerLine width="70%" height={12} />
            </div>
          </div>
        ))}
      </div>
    </ShimmerPageShell>
  );
}

export function AdminFormPageShimmer({ fields = 5, withHeader = true }) {
  return (
    <ShimmerPageShell label="設定を読み込み中">
      {withHeader && <AdminPageHeaderShimmer />}
      <div className="shimmer-form-block">
        {Array.from({ length: fields }, (_, i) => (
          <div key={`field-${i}`} className="shimmer-form-row">
            <ShimmerLine width="28%" height={14} />
            <Shimmer height={40} style={{ flex: 1 }} rounded="md" />
          </div>
        ))}
      </div>
      <Shimmer width={88} height={38} rounded="md" />
    </ShimmerPageShell>
  );
}

export function AdminTableShimmer({ rows = 6, columns = 4 }) {
  return (
    <div className="shimmer-table" style={{ '--shimmer-cols': columns }} role="status" aria-live="polite" aria-label="読み込み中">
      {Array.from({ length: rows }, (_, i) => (
        <div key={`table-row-${i}`} className="shimmer-table__row">
          {Array.from({ length: columns }, (_, j) => (
            <ShimmerLine key={`cell-${i}-${j}`} width={`${50 + ((i + j) % 3) * 15}%`} height={14} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function AdminTablePageShimmer({ rows = 6, columns = 4 }) {
  return (
    <ShimmerPageShell label="データを読み込み中">
      <AdminPageHeaderShimmer />
      <AdminListToolbarShimmer selects={2} />
      <AdminTableShimmer rows={rows} columns={columns} />
    </ShimmerPageShell>
  );
}

export function AdminInquiryListShimmer({ rows = 6, withHeader = true }) {
  const list = (
    <div className="shimmer-inquiry-list">
      {Array.from({ length: rows }, (_, i) => (
        <div key={`inq-${i}`} className="shimmer-inquiry-item">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <ShimmerLine width={`${30 + (i % 3) * 10}%`} height={16} />
            <Shimmer width={56} height={22} rounded="full" />
            <Shimmer width={72} height={22} rounded="full" />
          </div>
          <ShimmerLine width="55%" height={12} />
          <ShimmerLine width="90%" height={12} />
        </div>
      ))}
    </div>
  );

  if (!withHeader) {
    return (
      <div role="status" aria-live="polite" aria-label="お問い合わせを読み込み中">
        <AdminListToolbarShimmer selects={1} />
        {list}
      </div>
    );
  }

  return (
    <ShimmerPageShell label="お問い合わせを読み込み中">
      <AdminPageHeaderShimmer />
      <AdminListToolbarShimmer selects={1} />
      {list}
    </ShimmerPageShell>
  );
}

export function AdminCardGridShimmer({ cards = 6, withHeader = true }) {
  return (
    <ShimmerPageShell label="データを読み込み中">
      {withHeader && <AdminPageHeaderShimmer />}
      <div className="shimmer-card-grid">
        {Array.from({ length: cards }, (_, i) => (
          <div key={`card-${i}`} className="shimmer-card">
            <ShimmerLine width="60%" height={18} />
            <ShimmerLine width="90%" height={12} />
            <ShimmerLine width="75%" height={12} />
            <Shimmer width={72} height={32} rounded="md" />
          </div>
        ))}
      </div>
    </ShimmerPageShell>
  );
}

export function AdminHealthCheckShimmer() {
  return (
    <ShimmerPageShell label="ヘルスチェックを読み込み中">
      <AdminPageHeaderShimmer />
      <div className="shimmer-health-grid">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={`summary-${i}`} className="shimmer-health-card">
            <ShimmerLine width="55%" height={12} />
            <Shimmer width="45%" height={32} rounded="sm" />
          </div>
        ))}
      </div>
      <div className="shimmer-table" style={{ '--shimmer-cols': 5 }}>
        {Array.from({ length: 8 }, (_, i) => (
          <div key={`hc-row-${i}`} className="shimmer-table__row">
            {Array.from({ length: 5 }, (_, j) => (
              <ShimmerLine key={`hc-cell-${i}-${j}`} width={`${40 + ((i + j) % 4) * 12}%`} height={14} />
            ))}
          </div>
        ))}
      </div>
    </ShimmerPageShell>
  );
}

export function AdminDashboardSectionShimmer({ cards = 5 }) {
  return (
    <div className="shimmer-health-grid dashboard-health-summary-grid">
      {Array.from({ length: cards }, (_, i) => (
        <div key={`dash-section-${i}`} className="shimmer-health-card">
          <ShimmerLine width="55%" height={12} />
          <Shimmer width="45%" height={28} rounded="sm" />
        </div>
      ))}
    </div>
  );
}

export function AdminProductListShimmer({ cards = 4 }) {
  return (
    <section className="dashboard-products-section">
      <div className="dashboard-products-header">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <ShimmerLine width={120} height={18} />
          <ShimmerLine width={220} height={12} />
        </div>
        <Shimmer width={120} height={32} rounded="md" />
      </div>
      <div className="shimmer-card-grid">
        {Array.from({ length: cards }, (_, i) => (
          <div key={`dash-product-${i}`} className="shimmer-card">
            <ShimmerLine width="50%" height={16} />
            <ShimmerLine width="80%" height={12} />
            <Shimmer width={80} height={22} rounded="full" />
          </div>
        ))}
      </div>
    </section>
  );
}
